import {CSSProp} from 'styled-components';
import {breakpoints, spacing, container, borderRadius, boxShadow} from '../common';

const themeIds = [
  'nib',
  'navigator',
  'foundation',
  'qantas',
  'gu',
  'suncorp',
  'aami',
  'apia',
  'tal',
  'iman',
  'ing',
  'priceline',
  'nibLegacy',
  'worldNomads',
  'nsi',
  'cota',
  'australianSeniors',
  'real',
  'aanz',
  'edi',
  'tid'
] as const;
export const formTypes = ['light', 'white'] as const;
export type ThemeVariants = (typeof themeIds)[number];
export type FormVariants = (typeof formTypes)[number];

export interface PropsWithTheme {
  children?: React.ReactNode;
  theme: ThemeSchema;
  formMode?: string;
}

// Colors:

export interface ColorPairSchema {
  backgroundColor: string;
  textColor: string;
}

export interface ColorBrandSchema {
  base: string;
  light: string;
  dark: string;
}

export interface ColorShadesSchema {
  black: string;
  darkest: string;
  darker: string;
  dark: string;
  light: string;
  lighter: string;
  lightest: string;
  nearWhite: string;
  white: string;
}

export interface ColorProductSchema {
  hospital: string;
  extras: string;
  combined: string;
  highlight: ColorPairSchema;
}

export interface ColorsSchema {
  brand: ColorBrandSchema;
  accent: string;
  shades: ColorShadesSchema;
  focus: string;
  error: string;
  success: string;
  info: string;
  product: ColorProductSchema;
  trueGreen?: string;
  trueGreen80?: string;
  trueGreen60?: string;
  trueGreen40?: string;
  trueGreen20?: string;

  brightGreen?: string;
  brightGreen80?: string;
  brightGreen60?: string;
  brightGreen40?: string;
  brightGreen20?: string;

  sunsetPink?: string;
  sunsetPink80?: string;
  sunsetPink60?: string;
  sunsetPink40?: string;
  sunsetPink20?: string;
  sageGreen?: string;
  sageGreen80?: string;
  sageGreen60?: string;
  sageGreen40?: string;
  sageGreen20?: string;
  warmWhite?: string;
  warmWhite80?: string;
  warmWhite60?: string;
  warmWhite40?: string;
  warmWhite20?: string;
}

// Focus:

export interface FocusSchema {
  outlineColor: string;
  outlineStyle: string;
  outlineWidth: string;
  outlineOffset: string;
}

// Mode:

export interface ModeSchema {
  form: FormVariants;
}

// Typography:

export interface HeadingSchema {
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  lineHeight: number;
  tracking: string;
  uppercase: boolean;
}

export interface HeadingsSchema {
  fontFamily?: string;
  color: string;
  lineHeight?: number;
  h1: HeadingSchema;
  h2: HeadingSchema;
  h3: HeadingSchema;
  h4: HeadingSchema;
  h5: HeadingSchema;
  h6: HeadingSchema;
}

export interface CopySchema {
  color: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  lineHeight: number;
  smallFontSize: string;
  largeFontSize: string;
}

// Link:

export interface LinkSchema {
  loudColor: string;
  darkColor: string;
  lightColor: string;
  destructiveColor: string;
}

// Buttons:

export interface ButtonVariantSchema {
  dark: {
    normal: CSSProp;
    selected: CSSProp;
    disabled: CSSProp;
  };
  light: {
    normal: CSSProp;
    selected: CSSProp;
    disabled: CSSProp;
  };
  destructive: {
    normal: CSSProp;
    selected: CSSProp;
    disabled: CSSProp;
  };
}

export interface ButtonSchema {
  small: CSSProp;
  medium: CSSProp;
  large: CSSProp;
  primary: ButtonVariantSchema;
  secondary: ButtonVariantSchema;
  tertiary: ButtonVariantSchema;
}

// Forms:

export interface FormVariantSchema {
  borderColor: string;
  borderColorValid: string;
  borderColorInvalid: string;
  selectedToggleColor: string;
  helpTextColor: string;
  helpTextSize: string;
  labelColor: string;
  labelSize: string;
  errorMessageTextColor: string;
  infoMessageTextColor: string;
  backgroundColor: string;
  inputBackgroundColor: string;
  boxShadow: string;
}

export interface FormSchema {
  // Indexable with 'string' types
  [index: string]: FormVariantSchema;
  light: FormVariantSchema;
  white: FormVariantSchema;
}

// Small Footer:

export interface SmallFooterSchema extends ColorPairSchema {
  linkColor: string;
}

// Panel:

export interface PanelSchema {
  loud: ColorPairSchema;
  muted: ColorPairSchema;
  light: ColorPairSchema;
  dark: ColorPairSchema;
  white: ColorPairSchema;
}

export interface ThemeSchema {
  breakpoints: typeof breakpoints;
  container: typeof container;
  spacing: typeof spacing;
  borderRadius: typeof borderRadius;
  boxShadow: typeof boxShadow;
  typeScale: Record<number, string>;
  fonts: Record<string, string>;
  colors: ColorsSchema;
  focus: FocusSchema;
  id: ThemeVariants;
  mode: ModeSchema;
  headings: HeadingsSchema;
  copy: CopySchema;
  link: LinkSchema;
  button: ButtonSchema;
  forms: FormSchema;
  smallFooter: SmallFooterSchema;
  panel?: PanelSchema;
}
